import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { useToast } from '../../../../hooks/use-toast';
import BackButton from '../../../../components/admin/BackButton';
import ProductAutocomplete from '../../../../components/admin/procurement/ProductAutocomplete';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreatePurchaseRequest = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('admin_token');

  const [formData, setFormData] = useState({
    title: '',
    department: '',
    justification: '',
    urgency_level: 'medium',
    required_delivery_date: '',
    delivery_address: '',
    budget_code: '',
    currency: 'USD',
    notes: '',
  });

  const [items, setItems] = useState([
    { product_name: '', description: '', quantity: 1, unit: 'units', estimated_unit_price: 0, estimated_total: 0 }
  ]);

  const addItem = () => {
    setItems([...items, { product_name: '', description: '', quantity: 1, unit: 'units', estimated_unit_price: 0, estimated_total: 0 }]);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index][field] = value;

    if (field === 'quantity' || field === 'estimated_unit_price') {
      updatedItems[index].estimated_total = updatedItems[index].quantity * updatedItems[index].estimated_unit_price;
    }

    setItems(updatedItems);
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + item.estimated_total, 0);
  };

  const handleSubmit = async (submitForReview = false) => {
    try {
      if (!formData.title || !formData.department || !formData.justification) {
        toast({
          title: 'Validation Error',
          description: 'Please fill in all required fields',
          variant: 'destructive'
        });
        return;
      }

      if (items.length === 0 || items.some(item => !item.product_name || item.quantity <= 0)) {
        toast({
          title: 'Validation Error',
          description: 'Please add at least one valid item',
          variant: 'destructive'
        });
        return;
      }

      setLoading(true);

      const prData = {
        ...formData,
        items: items.map(item => ({
          product_name: item.product_name,
          description: item.description,
          quantity: item.quantity,
          unit: item.unit,
          estimated_unit_price: item.estimated_unit_price,
          estimated_total: item.estimated_total,
        }))
      };

      const response = await fetch(`${API_URL}/api/procurement/purchase-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(prData)
      });

      if (!response.ok) throw new Error('Failed to create PR');
      const createdPR = await response.json();

      if (submitForReview) {
        await fetch(`${API_URL}/api/procurement/purchase-requests/${createdPR.id}/submit`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ comments: 'Submitted for review' })
        });
      }

      toast({
        title: 'Success',
        description: submitForReview ? 'Purchase Request submitted for review' : 'Purchase Request created as draft'
      });

      navigate(`/admin/procurement/purchase-requests/${createdPR.id}`);
    } catch (error) {
      console.error('Error creating PR:', error);
      toast({
        title: 'Error',
        description: 'Failed to create purchase request',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <BackButton />

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-[#2B2D42]">Create Purchase Request</h2>
        <p className="text-sm text-[#6C757D]">Request approval for new purchases</p>
      </div>

      <Card className="mb-6">
        <CardContent className="pt-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Office Supplies Q2 2026"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                Department <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                placeholder="e.g., IT, Finance, Operations"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              Justification <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.justification}
              onChange={(e) => setFormData({ ...formData, justification: e.target.value })}
              rows={3}
              placeholder="Explain why this purchase is needed..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">Urgency Level</label>
              <select
                value={formData.urgency_level}
                onChange={(e) => setFormData({ ...formData, urgency_level: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">Required Delivery Date</label>
              <input
                type="date"
                value={formData.required_delivery_date}
                onChange={(e) => setFormData({ ...formData, required_delivery_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">Currency</label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="AED">AED</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">Budget Code</label>
              <input
                type="text"
                value={formData.budget_code}
                onChange={(e) => setFormData({ ...formData, budget_code: e.target.value })}
                placeholder="Optional"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">Delivery Address</label>
              <input
                type="text"
                value={formData.delivery_address}
                onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
                placeholder="Optional"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-[#2B2D42]">Items</h3>
            <Button onClick={addItem} size="sm" variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          </div>

          <div className="space-y-4">
            {items.map((item, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-xs text-[#6C757D] mb-1">Product Name *</label>
                    <ProductAutocomplete
                      value={item.product_name}
                      onChange={(name) => updateItem(index, 'product_name', name)}
                      onSelectSuggestion={(suggestion) => {
                        updateItem(index, 'product_name', suggestion.product_name);
                        if (suggestion.last_price) {
                          updateItem(index, 'estimated_unit_price', suggestion.last_price);
                        }
                      }}
                      placeholder="Type to search products..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[#6C757D] mb-1">Quantity *</label>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', Number(e.target.value))}
                      min="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[#6C757D] mb-1">Unit</label>
                    <input
                      type="text"
                      value={item.unit}
                      onChange={(e) => updateItem(index, 'unit', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[#6C757D] mb-1">Estimated Unit Price *</label>
                    <input
                      type="number"
                      value={item.estimated_unit_price}
                      onChange={(e) => updateItem(index, 'estimated_unit_price', Number(e.target.value))}
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
                    />
                  </div>
                  <div className="flex items-end">
                    {items.length > 1 && (
                      <Button
                        onClick={() => removeItem(index)}
                        variant="outline"
                        size="sm"
                        className="w-full border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
                {item.estimated_total > 0 && (
                  <div className="mt-2 text-sm text-[#6C757D]">
                    Total: {formData.currency} {item.estimated_total.toFixed(2)}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex justify-end">
              <div className="text-right">
                <p className="text-sm text-[#6C757D]">Estimated Total</p>
                <p className="text-2xl font-bold text-[#00B4D8]">
                  {formData.currency} {calculateTotal().toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardContent className="pt-6">
          <label className="block text-sm font-medium text-[#2B2D42] mb-2">Additional Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={3}
            placeholder="Optional notes..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
          />
        </CardContent>
      </Card>

      <div className="flex justify-end space-x-4">
        <Button
          onClick={() => handleSubmit(false)}
          disabled={loading}
          variant="outline"
        >
          Save as Draft
        </Button>
        <Button
          onClick={() => handleSubmit(true)}
          disabled={loading}
          className="bg-[#00B4D8] hover:bg-[#0096B8]"
        >
          {loading ? 'Creating...' : 'Submit for Review'}
        </Button>
      </div>
    </div>
  );
};

export default CreatePurchaseRequest;